import { useState, useEffect } from 'react'
import { Notification } from 'baseui/notification';
import { Button, SHAPE, ButtonProps, KIND, SIZE } from 'baseui/button';
import { useCreateObjectMutation, CreateObjectInstanceCommand } from '../../services/objects'
import { ProgressSteps, NumberedStep } from "baseui/progress-steps";
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton } from 'baseui/modal';
import { FormControl } from "baseui/form-control";
import { Input } from "baseui/input";
import { ParagraphSmall, ParagraphXSmall } from 'baseui/typography';
import { Select } from 'baseui/select';
import { getUserId } from '../../services/silentAuth';
import { IAutomationObject } from '../../automationObjects';
import { ObjectNavigatorModal } from '../ObjectSelector/Modal';
import { ListItem, ListItemLabel } from 'baseui/list';
import { useReportMutationState } from '../../hooks/useReportMutationState';
import { uid } from 'uid';
import { useGetUsersResourceGroupsQuery } from '../../services/resourceGroups/userApi';
import { TCompany } from '../../automationObjects/Agents/Organizations/Company/types';


export interface CreateObjectDialogProps {
    schema: string,
    defaultProperties: any,
    object?: IAutomationObject,
}
const getLastNameFormSchema = (schema: string) => {
    const arr = schema.split('.');
    return arr[arr.length-1]
}
export const CreateObjectDialog = ({ schema, defaultProperties, object }: CreateObjectDialogProps) => {
    const [createObject, createObjectResult] = useCreateObjectMutation();

    const [name, setName] = useState(`${getLastNameFormSchema(schema)}-${uid()}`);
    const [description, setDescription] = useState("");
    const [parent, setParent] = useState(object?.id);
    const [current, setCurrent] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [showObjectSelector, setShowObjectSelector] = useState(false);
    const [selectedParent, setSeletctedParent] = useState<IAutomationObject>();

    const onNameChange = (e: any) => setName(e.target.value);
    const onDescriptionChange = (e: any) => setDescription(e.target.value);
    const onParentChange = (e: any) => setParent(e.target.value);
    function onShowObjectSelector() { setShowObjectSelector(true); }
    const onParentSelected = (item: IAutomationObject) => setSeletctedParent(item);

    useReportMutationState(createObjectResult, () => { setIsOpen(false); setCurrent(0); })


    // resource group 
    // get resource groups for this account
    const userId = getUserId();
    const { data: allowedResourceGroups, isSuccess } = useGetUsersResourceGroupsQuery(userId ?? '', { skip: !userId });
    const [resourceGroup, setResourceGroup] = useState<any>();
    const resourceGroupsSelect = allowedResourceGroups?.map((item: TCompany, index: number) => { return { label: item.name, id: item.id } });
    const onResourceGroupChanged = (e: any) => { setResourceGroup(e.value[0]); }

    function onCreateClick(): void {

        const cmd: CreateObjectInstanceCommand = {
            name: name,
            resourceGroupId: object ? object.resourceGroupId : selectedParent?.resourceGroupId ? selectedParent?.resourceGroupId : resourceGroup.id,
            description: description,
            schema: schema,
            parentId: object ? object.id : selectedParent?.id,
            properties: defaultProperties
        }
        createObject(cmd);
    }


    useEffect(() => {
        if (isSuccess) {
            const rgfound = allowedResourceGroups?.filter((item, index) => item.id === selectedParent?.resourceGroupId)[0];
            setResourceGroup({ label: rgfound?.name, id: rgfound?.id });
        }
    }, [isSuccess]);

    return (
        <>
            <Button onClick={() => setIsOpen(true)} shape='pill' kind='secondary'
                overrides={{
                    BaseButton: { style: { width: "100%" } }
                }}
            >
                Create
            </Button>
            <Modal closeable onClose={() => setIsOpen(false)} isOpen={isOpen}>
                <ModalHeader>Create new Object</ModalHeader>
                <ModalBody>
                    <ParagraphSmall>{schema}</ParagraphSmall>
                    {object && parent && <ListItem>
                        <ListItemLabel description={object.description}>
                            {object.name}
                        </ListItemLabel>
                    </ListItem>}
                    {createObjectResult.error &&
                        <Notification kind='negative'>
                            There has been an error loading data: {JSON.stringify(createObjectResult.error)}
                        </Notification>
                    }
                    <ProgressSteps current={current}>
                        <NumberedStep title="Select Name">
                            <FormControl
                                label={() => "Name"}
                                caption={() => "Enter a name for the new object"}
                            >
                                <Input autoFocus value={name} onChange={onNameChange} required placeholder="Enter the objects name ..." />
                            </FormControl>
                            <SpacedButton onClick={() => setCurrent(1)} disabled={!name}>Next</SpacedButton>
                        </NumberedStep >
                        <NumberedStep title="Description">
                            <FormControl
                                label={() => "Description"}
                                caption={() => "Enter a description for the new object"}
                            >
                                <Input autoFocus value={description} required onChange={onDescriptionChange} placeholder="Enter a description ..." />
                            </FormControl>
                            <SpacedButton onClick={() => setCurrent(0)} >Back</SpacedButton>
                            <SpacedButton onClick={() => object ? setCurrent(3) : setCurrent(2)} >Next</SpacedButton>
                        </NumberedStep >

                        {!object && <NumberedStep title="Parent">

                            <ParagraphXSmall>Optional: Select a parent App</ParagraphXSmall>
                            <Button onClick={onShowObjectSelector}>Select Parent</Button>
                            {selectedParent &&
                                <ListItem>
                                    <ListItemLabel description={selectedParent.description}>
                                        {selectedParent.name}
                                    </ListItemLabel>
                                </ListItem>
                            }
                            {showObjectSelector &&
                                <ObjectNavigatorModal title='Select App as parent'
                                    isOpen={showObjectSelector}
                                    showAddObject
                                    setIsOpen={(e: boolean) => setShowObjectSelector(e)}
                                    onSelected={onParentSelected}
                                    currentSelectedParent={selectedParent}></ObjectNavigatorModal>
                            }

                            {!selectedParent && (<FormControl
                                label={() => "Resource Group"}
                                caption={() => "Select the resource Group (only if no parent is set)"}
                            >
                                <Select
                                    placeholder=''
                                    options={resourceGroupsSelect}
                                    value={resourceGroup}
                                    onChange={onResourceGroupChanged}
                                />
                            </FormControl>)}


                            <SpacedButton onClick={() => setCurrent(1)} >Back</SpacedButton>
                            <SpacedButton onClick={() => setCurrent(3)} disabled={!parent && !resourceGroup} >Next</SpacedButton>
                        </NumberedStep >
                        }

                        <NumberedStep title="Verify & Create">
                            <ParagraphSmall>Name: {name}</ParagraphSmall>
                            <ParagraphSmall>Description: {description}</ParagraphSmall>
                            <ParagraphSmall>Type: {schema}</ParagraphSmall>
                            <SpacedButton onClick={() => object ? setCurrent(1) : setCurrent(2)} >Back</SpacedButton>
                        </NumberedStep >

                    </ProgressSteps>

                </ModalBody>
                <ModalFooter>
                    <ModalButton kind="tertiary" onClick={() => { setIsOpen(false); setCurrent(0); }}>
                        Cancel
                    </ModalButton>
                    <ModalButton onClick={onCreateClick} disabled={current < 1} isLoading={createObjectResult.isLoading}>Create</ModalButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

function SpacedButton(props: ButtonProps) {
    return (
        <Button
            {...props}
            shape={SHAPE.pill}
            kind={KIND.secondary}
            size={SIZE.compact}
            overrides={{
                BaseButton: {
                    style: ({ $theme }) => ({
                        marginLeft: $theme.sizing.scale200,
                        marginRight: $theme.sizing.scale200,
                        marginTop: $theme.sizing.scale800,
                    }),
                },
            }}
        />
    );
}
